/* tslint:disable:variable-name no-inferrable-types */

import { QuadTreeObject } from './quad.tree.object';
import { intersectBox }   from './util';

/**
 *
 * @class QuadTree
 *
 * @brief simple quad tree for bounding box based objects (normally train Movement objects).
 *
 * @link https://gamedevelopment.tutsplus.com/tutorials/quick-tip-use-quadtrees-to-detect-likely-collisions-in-2d-space--gamedev-374
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2019
 *
 */
export class QuadTree {

  /** @var QuadTreeObject[] the objects we are tracking (normally train Movement objects */
  private        _objects: QuadTreeObject[] = [];
  private              _N: number           = 0;

  /** @var QuadTree[]  the subtrees (area split into quadrants) */
  private           _nodes: QuadTree[]       = [];

  /** @var QuadTree|null the next level up (bigger area */
  private readonly _parent: QuadTree|null    = null;

  /** @var number the max level for this subtree */
  private      _treeHeight: number           = 0;

  /** @var number the depth of this bounding area */
  private  readonly _level: number           = 0;

  /** @var number x position of this bounding area */
  private      readonly _x: number           = 0;

  /** @var number y position of this bounding area */
  private      readonly _y: number           = 0;

  /** @var number the width of this bounding area */
  private  readonly _width: number           = 0;

  /** @var number the height of this bounding area */
  private readonly _height: number           = 1;

  /** @var number max items in a node before we split */
  private      _bucketSize: number           = 10;

  /** @brief standard constructor */
  constructor(parent: QuadTree|null, level: number, x: number, y: number, width: number, height: number) {

    this._parent = parent;

    this._level  = level;

    this._x      = x;
    this._y      = y;
    this._width  = width;
    this._height = height;

    if (this._parent == null) {

      this._x -= 2;
      this._width += 4;
      this._y -= 2;
      this._height += 4;
    }
  }

  /** @brief fetch the objects we are tracking */
  objects(): QuadTreeObject[] {
    return this._objects;
  }

  /** @brief fetch the level of this subtree */
  level(): number {
    return this._level;
  }

  /** @brief fetch the total height of the subtree */
  height(): number {

    return this._treeHeight;
  }

  size(): number {

    return this._N;
  }

  /* empty out this subtree */
  public clear(): void {

    this._objects.length = 0;
    this._nodes.length   = 0;
    this._treeHeight     = 0;
  }

  /** @brief helper to split into sub-nodes */
  private  split(): void {

    const sw: number = this._width / 2;
    const sh: number = this._height / 2;
    const  x: number = this._x;
    const  y: number = this._y;

    this._nodes.push(new QuadTree(this, this._level + 1, x + sw,       y + sh, sw, sh));
    this._nodes.push(new QuadTree(this, this._level + 1,       x,        y + sh, sw, sh));
    this._nodes.push(new QuadTree(this, this._level + 1,        x, y, sw, sh));
    this._nodes.push(new QuadTree(this, this._level + 1, x + sw, y, sw, sh));
  }

  /** @brief helper to find which quadrant an object is supposed to be in, -1 means it doesn't fit */
  private index(x: number, y: number, width: number, height: number): number {

    let   loc: number = -1;
    const  ym: number  = this._x + (this._width / 2);
    const  xm: number  = this._y + (this._height / 2);

    /* Object can completely fit within the top quadrants */

    const  tq: boolean  = ((y > xm) && ((y + height) < (this._y + this._height)));

    /* Object can completely fit within the bottom quadrants */

    const  bq: boolean = (y > this._y) && ((y + height) < xm);

    if ((tq === false) && (bq === false)) {
      // console.log("idx: " + " xm: " + xm + " (y): " + this._y + " (h): " + this._height);
    }

    if ((x > this._x) && ((x + width) < ym)) {

      /* Object can completely fit within the left quadrants */

      if (tq) {

        loc = 1;

      } else if (bq) {

        loc = 2;
      }

    } else if ((x > ym) && ((x + width) < (this._x + this._width))) {

      /* Object can completely fit within the right quadrants */

      if (tq) {

        loc = 0;

      } else if (bq) {

        loc = 3;
      }

    } else {

        // console.log("idx: " + " ym: " + ym + " (x): " + this._x + " (h): " + this._width);

    }

    return loc;
  }

  /** @brief helper to find which quadrant an object is supposed to be in, -1 means it doesn't fit */
  private indexOfPoint(x: number, y: number): number {

    let  loc: number = -1;
    const ym: number  = this._x + (this._width / 2);
    const xm: number  = this._y + (this._height / 2);

    /* Object can completely fit within the top quadrants */

    const tq: boolean  = (y > xm);

    /* Object can completely fit within the bottom quadrants */

    const bq: boolean = (y < xm);

    if (x < ym) {

      /* Object can completely fit within the left quadrants */

      if (tq) {

        loc = 1;

      } else if (bq) {

        loc = 2;
      }

    } else if (x > ym) {

      /* Object can completely fit within the right quadrants */

      if (tq) {

        loc = 0;

      } else if (bq) {

        loc = 3;
      }
    }

    return loc;
  }

  /** @brief insert a new object into the tree (returns total tree height) */
  public insert(object: QuadTreeObject): number {

    this._N++;

    // console.log("ins: " + object.objId() + ' ' + object.x() + ',' +  object.y() + ' ' + object.width() + 'x' + object.height());

    /* does it fit in an existing subtree? */

    if (this._nodes.length !== 0) {

      const index: number = this.index(object.x(), object.y(), object.width(), object.height());

      if (index !== -1) {

        const tmp: number = 1 + this._nodes[index].insert(object);

        if (tmp > this._treeHeight) {

          this._treeHeight = tmp;
        }

        return this._treeHeight;
      }
    }

    /* add to this container */

    this._objects.push(object);

    /*
    console.log("obj: " + object.objId() + ' ' + object.x() + ',' +  object.y() + ' ' + object.width() + 'x' +
       object.height() + ' in ' + this._x + ',' + this._y + ' - ' + (this._x + this._width) + ',' + (this._y +
       this._height));
     */

    /* hold off on splitting node until we have to. */

    if (this._objects.length <= this._bucketSize) {

      return this._treeHeight;
    }

    /* node is split already? */

    if (this._nodes.length === 0) {

      this.split();
      this._treeHeight++;
    }

    /* distribute the current objects over the sub-nodes (if possible) */

    const keepers: QuadTreeObject[] = [];

    for (const child of this._objects) {

      const loc: number = this.index(child.x(), child.y(), child.width(), child.height());

      if (loc === -1) {

        /* must stay in parent */

        keepers.push(child);

        /*
        console.log("kep: " + child.objId() + ' ' + child.x() + ',' +  child.y() + ' ' + child.width() + 'x' +
         child.height() + ' in ' + this._x + ',' + this._y + ' - ' + (this._x + this._width) + ',' +
          (this._y + this._height));
         */

      } else {

        // console.log("dwn: " + child.objId());

        this._nodes[loc].insert(child);
      }
    }

    this._objects = keepers.slice();

    return this._treeHeight;
  }

  /** @brief fetch all objects from the node that contains the given point */
  public find(x: number, y: number): QuadTreeObject[] {

    const  loc: number           = this.indexOfPoint(x, y);
    let result: QuadTreeObject[] = [];

    if ((loc !== -1) && (this._nodes.length !== 0)) {

      /* find any matching sub-node items */

      result = this._nodes[loc].find(x, y);
    }

    /* add in the parent level objects which don't fit neatly into sub-nodes */

    for (const item of this._objects) {

      result.push(item);
    }

    return result;
  }

  /** @brief like find(), but now we look for anything that intersects with this line */
  public intersects(px: number, py: number, qx: number, qy: number): QuadTreeObject[] {

    let result: QuadTreeObject[] = [];

    /*
     * for each of the child nodes, we must test if this line cuts through the node;
     * nodes the line cuts through, might have an intersection with a train path inside
     * that bounding box.
     *
     */

    for (const item of this._nodes) {

      /* is this sub-quad even possible? */

      let hit: {x: number, y: number} = {x: 0, y: 0};

      if (!intersectBox(
        item._x,
        item._y,
        item._width,
        item._height,
        px, py, qx, qy,
        hit)) {

        continue ;
      }

      /* ok get any results from this quad */

      const quadResult: QuadTreeObject[] = item.intersects(px, py, qx, qy);

      result = result.concat(quadResult);
    }

    /* append objects from this level that have an actual intersection */

    for (const item of this._objects) {

      if (item.intersects(px, py, qx, qy)) {

        result.push(item);
      }
    }

    return result;
  }
}
